import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { TASK_STATUSES } from '@capforge/shared';
import type { TaskDto } from '@capforge/shared';
import { api, errorMessage } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import {
  compactJson,
  formatConfidence,
  formatDateTime,
  formatLatency,
  formatTimeShort,
  prettyJson,
  truncate,
} from '../lib/format';
import { Table } from '../components/Table';
import type { Column } from '../components/Table';
import { Badge, sourceTone, statusTone } from '../components/Badge';
import { Modal } from '../components/Modal';
import { ErrorBox } from '../components/ErrorBox';
import { Spinner } from '../components/Spinner';

/** 50 dong moi trang: du de nhin ra xu huong ma van tai nhanh tren SQLite. */
const PAGE_SIZE = 50;

export function Tasks() {
  const [status, setStatus] = useState('');
  const [profileKey, setProfileKey] = useState('');
  const [profileDraft, setProfileDraft] = useState('');
  const [apiKeyId, setApiKeyId] = useState<number | null>(null);

  const [items, setItems] = useState<TaskDto[]>([]);
  const [nextCursor, setNextCursor] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<TaskDto | null>(null);

  const keys = useAsync((signal) => api.keys(signal), []);

  const load = useCallback(
    (cursor: number | null): void => {
      setLoading(true);
      setError(null);
      // Tai lai tu dau thi cursor cu khong con y nghia — an nut "Tai them" cho toi khi
      // co trang moi, neu khong se tai them bang cursor cua bo loc truoc.
      if (cursor === null) setNextCursor(null);
      api
        .tasks({
          limit: PAGE_SIZE,
          cursor,
          status: status === '' ? undefined : status,
          profileKey: profileKey === '' ? undefined : profileKey,
          apiKey: apiKeyId,
        })
        .then(
          (page) => {
            setLoading(false);
            // cursor === null nghia la tai lai tu dau -> thay the; nguoc lai la "tai them".
            setItems((prev) => (cursor === null ? page.items : [...prev, ...page.items]));
            setNextCursor(page.nextCursor);
          },
          (err: unknown) => {
            setLoading(false);
            setError(errorMessage(err));
          },
        );
    },
    [status, profileKey, apiKeyId],
  );

  useEffect(() => {
    load(null);
  }, [load]);

  const columns = useMemo<Column<TaskDto>[]>(
    () => [
      {
        key: 'id',
        header: 'ID',
        className: 'whitespace-nowrap cf-mono',
        render: (row) => (
          <div>
            <div>{row.id}</div>
            <div className="text-xs text-slate-500 dark:text-slate-400">
              {formatTimeShort(row.createdAt)}
            </div>
          </div>
        ),
      },
      {
        key: 'type',
        header: 'Loại',
        render: (row) => (
          <div>
            <div className="cf-mono text-xs">{row.taskType}</div>
            <div className="text-xs text-slate-500 dark:text-slate-400">
              {row.profileKey ?? 'chua co profile'}
            </div>
          </div>
        ),
      },
      {
        key: 'project',
        header: 'Project',
        render: (row) => (
          <span className="text-xs">{row.apiKeyLabel ?? (row.apiKeyId === null ? '—' : `#${row.apiKeyId}`)}</span>
        ),
      },
      {
        key: 'status',
        header: 'Trạng thái',
        render: (row) => (
          <div className="flex flex-col items-start gap-1">
            <Badge tone={statusTone(row.status)}>{row.status}</Badge>
            {row.errorCode !== null && (
              <span className="cf-mono text-xs text-red-700 dark:text-red-400">
                {row.errorCode}
              </span>
            )}
          </div>
        ),
      },
      {
        key: 'source',
        header: 'Nguon',
        render: (row) => (
          <div className="flex flex-col items-start gap-1">
            <Badge tone={sourceTone(row.source)}>{row.source ?? '—'}</Badge>
            {row.solverName !== null && (
              <span className="cf-mono text-xs text-slate-500 dark:text-slate-400">
                {row.solverName}
              </span>
            )}
          </div>
        ),
      },
      {
        key: 'solution',
        header: 'Đáp án',
        render: (row) => (
          <span className="cf-mono text-xs">{truncate(compactJson(row.solution), 44)}</span>
        ),
      },
      {
        key: 'shadow',
        header: 'Shadow',
        render: (row) =>
          row.shadowLocal === null ? (
            <span className="text-xs text-slate-400 dark:text-slate-600">—</span>
          ) : (
            <Badge tone={row.shadowMatch === true ? 'success' : 'danger'}>
              {row.shadowMatch === true ? 'khop' : 'lech'}
            </Badge>
          ),
      },
      {
        key: 'metrics',
        header: 'Độ tin / độ trễ',
        className: 'whitespace-nowrap',
        render: (row) => (
          <div className="text-xs">
            <div>{formatConfidence(row.confidence)}</div>
            <div className="text-slate-500 dark:text-slate-400">{formatLatency(row.latencyMs)}</div>
          </div>
        ),
      },
      {
        key: 'reported',
        header: 'Báo cáo',
        render: (row) =>
          row.reported === null ? (
            <span className="text-xs text-slate-400 dark:text-slate-600">—</span>
          ) : (
            <Badge tone={row.reported === 'correct' ? 'success' : 'danger'}>{row.reported}</Badge>
          ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">Task gần đây</h1>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Bam vao mot dong de xem chi tiet dap an, nguon va so sanh shadow.
          </p>
        </div>
        <button type="button" className="cf-btn cf-btn-ghost" onClick={() => load(null)}>
          Lam moi
        </button>
      </div>

      <section className="cf-card p-3">
        <form
          className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
          onSubmit={(event) => {
            event.preventDefault();
            setProfileKey(profileDraft.trim());
          }}
        >
          <div>
            <label className="cf-label" htmlFor="f-status">
              Trạng thái
            </label>
            <select
              id="f-status"
              className="cf-select"
              value={status}
              onChange={(event) => setStatus(event.target.value)}
            >
              <option value="">Tất cả</option>
              {TASK_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="cf-label" htmlFor="f-profile">
              Profile
            </label>
            <div className="flex gap-2">
              <input
                id="f-profile"
                className="cf-input cf-mono"
                placeholder="vd: __unknown__"
                value={profileDraft}
                onChange={(event) => setProfileDraft(event.target.value)}
              />
              <button type="submit" className="cf-btn cf-btn-ghost shrink-0">
                Loc
              </button>
            </div>
          </div>

          <div>
            <label className="cf-label" htmlFor="f-apikey">
              Project (API key)
            </label>
            <select
              id="f-apikey"
              className="cf-select"
              value={apiKeyId === null ? '' : String(apiKeyId)}
              onChange={(event) => {
                const value = event.target.value;
                setApiKeyId(value === '' ? null : Number(value));
              }}
            >
              <option value="">Tất cả</option>
              {(keys.data ?? []).map((key) => (
                <option key={key.id} value={key.id}>
                  {key.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              type="button"
              className="cf-btn cf-btn-ghost"
              onClick={() => {
                setStatus('');
                setProfileDraft('');
                setProfileKey('');
                setApiKeyId(null);
              }}
            >
              Xoa bo loc
            </button>
          </div>
        </form>
      </section>

      <section className="cf-card">
        <Table
          columns={columns}
          rows={items}
          rowKey={(row) => row.id}
          loading={loading}
          error={error}
          onRetry={() => load(null)}
          onRowClick={(row) => setSelected(row)}
          isActive={(row) => selected !== null && selected.id === row.id}
          emptyMessage="Không có task nào khớp bộ lọc"
          emptyHint="Gọi thử /createTask hoặc /v1/solve rồi quay lại trang này."
        />
        {nextCursor !== null && (
          <div className="flex justify-center border-t border-slate-200 p-3 dark:border-slate-800">
            <button
              type="button"
              className="cf-btn cf-btn-ghost"
              disabled={loading}
              onClick={() => load(nextCursor)}
            >
              {loading ? <Spinner label="Đang tải" /> : `Tai them ${PAGE_SIZE} dong`}
            </button>
          </div>
        )}
      </section>

      <TaskDetailModal task={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chi tiet task
// ---------------------------------------------------------------------------

function TaskDetailModal({ task, onClose }: { task: TaskDto | null; onClose: () => void }) {
  const taskId = task?.id ?? null;
  // Tai lai ban day du: bang danh sach co the da cat bot solution cho nhe.
  const detail = useAsync<TaskDto | null>(
    (signal) => (taskId === null ? Promise.resolve(null) : api.task(taskId, signal)),
    [taskId],
  );

  if (task === null) return null;
  // Loi tai chi tiet khong duoc lam mat man hinh — lui ve du lieu co san tren dong.
  // Kiem tra id vi useAsync giu lai du lieu cu trong luc dang tai task khac.
  const fresh = detail.data;
  const row: TaskDto = fresh !== null && fresh.id === task.id ? fresh : task;
  const isShadow = row.shadowLocal !== null;

  return (
    <Modal open={true} title={`Task #${row.id}`} onClose={onClose} size="lg">
      <div className="space-y-4">
        {detail.error !== null && (
          <ErrorBox
            title="Không tải được bản đầy đủ"
            message={`${detail.error} — dang hien du lieu tu bang danh sach.`}
            onRetry={detail.reload}
          />
        )}

        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm md:grid-cols-4">
          <Field label="Trạng thái">
            <Badge tone={statusTone(row.status)}>{row.status}</Badge>
          </Field>
          <Field label="Nguon">
            <Badge tone={sourceTone(row.source)}>{row.source ?? '—'}</Badge>
          </Field>
          <Field label="Solver">
            <span className="cf-mono">{row.solverName ?? '—'}</span>
          </Field>
          <Field label="Profile">
            <span className="cf-mono">{row.profileKey ?? '—'}</span>
          </Field>

          <Field label="Loại task">
            <span className="cf-mono">{row.taskType}</span>
          </Field>
          <Field label="Độ tin cậy">{formatConfidence(row.confidence)}</Field>
          <Field label="Độ trễ">{formatLatency(row.latencyMs)}</Field>
          <Field label="Job ID">
            <span className="cf-mono break-all">{row.jobId ?? '—'}</span>
          </Field>

          <Field label="Project">
            {row.apiKeyLabel ?? (row.apiKeyId === null ? '—' : `#${row.apiKeyId}`)}
          </Field>
          <Field label="Báo cáo">
            {row.reported === null ? (
              '—'
            ) : (
              <Badge tone={row.reported === 'correct' ? 'success' : 'danger'}>{row.reported}</Badge>
            )}
          </Field>
          <Field label="Tạo lúc">{formatDateTime(row.createdAt)}</Field>
          <Field label="Xong luc">{formatDateTime(row.finishedAt)}</Field>

          <Field label="Mã lỗi">
            {row.errorCode === null ? (
              '—'
            ) : (
              <span className="cf-mono text-red-700 dark:text-red-400">{row.errorCode}</span>
            )}
          </Field>
          <Field label="Upstream ID">
            <span className="cf-mono break-all">{row.upstreamId ?? '—'}</span>
          </Field>
          <Field label="Chi phí upstream">
            {row.upstreamCost === null ? '—' : `$${row.upstreamCost}`}
          </Field>
          <Field label="Shadow khop">
            {row.shadowMatch === null ? (
              '—'
            ) : (
              <Badge tone={row.shadowMatch ? 'success' : 'danger'}>
                {row.shadowMatch ? 'khop' : 'lech'}
              </Badge>
            )}
          </Field>
        </dl>

        {isShadow ? (
          <ShadowCompare delivered={row.solution} local={row.shadowLocal} />
        ) : (
          <div>
            <h3 className="mb-1 text-sm font-semibold">Đáp án trả về client</h3>
            <pre className="cf-mono max-h-72 overflow-auto rounded-md border border-slate-200 bg-slate-50 p-2 dark:border-slate-800 dark:bg-slate-950">
              {prettyJson(row.solution)}
            </pre>
          </div>
        )}

        {detail.loading && (
          <div className="flex justify-center">
            <Spinner label="Đang tải bản đầy đủ" />
          </div>
        )}
      </div>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </dt>
      <dd className="mt-0.5 text-slate-800 dark:text-slate-200">{children}</dd>
    </div>
  );
}

/**
 * So sanh canh nhau dap an noi bo va dap an da tra ve client (cua upstream).
 *
 * Day la man hinh de tin duoc solver: khong chi bao "khop / lech" ma chi ro LECH O DAU.
 * To do theo tung khoa, khong to ca khoi — lech mot ky tu trong mot truong thi chi
 * truong do dang ngo, cac truong con lai van dung.
 */
function ShadowCompare({
  delivered,
  local,
}: {
  delivered: Record<string, unknown> | null;
  local: Record<string, unknown> | null;
}) {
  const keys = Array.from(
    new Set([...Object.keys(delivered ?? {}), ...Object.keys(local ?? {})]),
  ).sort();

  return (
    <div>
      <h3 className="mb-1 text-sm font-semibold">So sánh shadow</h3>
      <p className="mb-2 text-xs text-slate-500 dark:text-slate-400">
        Che do shadow: dap an cua upstream duoc tra ve client, dap an noi bo chi de do. Dong to do
        la cho hai ben khac nhau.
      </p>
      <div className="overflow-x-auto rounded-md border border-slate-200 dark:border-slate-800">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800">
            <tr>
              <th className="cf-th">Truong</th>
              <th className="cf-th">Nội bộ (chỉ đo)</th>
              <th className="cf-th">Đã trả về client</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {keys.length === 0 ? (
              <tr>
                <td className="cf-td text-slate-500 dark:text-slate-400" colSpan={3}>
                  Ca hai ben deu rong.
                </td>
              </tr>
            ) : (
              keys.map((key) => {
                const localValue = local === null ? undefined : local[key];
                const deliveredValue = delivered === null ? undefined : delivered[key];
                const same = compactJson(localValue) === compactJson(deliveredValue);
                return (
                  <tr
                    key={key}
                    className={
                      same ? '' : 'bg-red-50 dark:bg-red-950/60'
                    }
                  >
                    <td className="cf-td cf-mono whitespace-nowrap">{key}</td>
                    <td
                      className={`cf-td cf-mono break-all ${same ? '' : 'font-semibold text-red-800 dark:text-red-300'}`}
                    >
                      {compactJson(localValue)}
                    </td>
                    <td
                      className={`cf-td cf-mono break-all ${same ? '' : 'font-semibold text-red-800 dark:text-red-300'}`}
                    >
                      {compactJson(deliveredValue)}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Noi bo (JSON)
          </h4>
          <pre className="cf-mono max-h-56 overflow-auto rounded-md border border-slate-200 bg-slate-50 p-2 dark:border-slate-800 dark:bg-slate-950">
            {prettyJson(local)}
          </pre>
        </div>
        <div>
          <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Da tra ve client (JSON)
          </h4>
          <pre className="cf-mono max-h-56 overflow-auto rounded-md border border-slate-200 bg-slate-50 p-2 dark:border-slate-800 dark:bg-slate-950">
            {prettyJson(delivered)}
          </pre>
        </div>
      </div>
    </div>
  );
}
